import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Input, Label } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { authClient } from '../../lib/auth-client';

/**
 * Separate admin login route per the brief, but reuses the same Better
 * Auth email/password flow as the customer login — there isn't a
 * separate "admin" auth system, just a different entry URL and redirect
 * target. Role-based access is enforced server-side regardless of which
 * login page was used to establish the session.
 */
export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) throw new Error(signInError.message ?? 'Sign in failed.');
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <SEOHead title="Admin Login — Bharat Consultancy" description="" canonicalPath="/admin/login" />

      <section className="flex min-h-[70vh] items-center justify-center bg-paper px-4 py-12">
        <div className="w-full max-w-sm rounded-lg border border-line bg-white p-8 shadow-stamp">
          <h1 className="text-center font-display text-2xl font-bold text-ink">Admin Login</h1>
          <p className="mt-1 text-center text-sm text-ink/60">Staff access only.</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
